// <Sentence> — canonical ClientHub kit component (Epic 1 · S1.1).
// Renders a composed `says` string VERBATIM at a meaning tint (warm/cool/positive).
// The page never edits the text; it is composed server-side. Never in a
// fixed-height container and never clamped to a line count. A caller may cut it
// to one line with CSS alone (a dense row), provided the whole text stays in the
// DOM and in the element's `title`. Renders alone — never coupled into a chart
// or card.
import React from "react";
import { tintClasses } from "../../shared/tints";

const SIZES = {
  none: "", // caller owns all sizing via className (Effort rows)
  sm: "text-sm",
  md: "text-sm sm:text-base",
  lg: "text-base sm:text-lg font-semibold", // row headline — the largest text in a row
  glass: "text-base sm:text-lg font-medium", // the takeaway panel
};

/**
 * @param {object} props
 * @param {string} [props.says]            Sentence text, rendered as-is (hub).
 * @param {string} [props.text]            Alias for callers (Effort) that hold
 *                                         the string in `text` instead of `says`.
 * @param {React.ReactNode} [props.children] Fallback content.
 * @param {"warm"|"cool"|"positive"} [props.tint="cool"]
 * @param {"none"|"sm"|"md"|"lg"|"glass"} [props.size="md"]
 * @param {keyof JSX.IntrinsicElements} [props.as="p"]
 * @param {string} [props.className]       Applied after the tint text.
 */
export default function Sentence({
  says,
  text,
  children,
  tint = "cool",
  size = "md",
  as: Tag = "p",
  className = "",
  ...rest
}) {
  const t = tintClasses(tint);
  const emphasised = size === "lg" || size === "glass";
  const content = says ?? text ?? children;
  return (
    <Tag
      className={`${SIZES[size] ?? SIZES.md} ${emphasised ? t.text : ""} ${className}`}
      {...rest}
    >
      {content}
    </Tag>
  );
}
